import { View } from 'react-native';
import { useDeviceSize, DEVICE_SIZES, CreateResponsiveStyle } from 'rn-responsive-styles';
import { LinearGradient } from 'expo-linear-gradient';
import { isFuture } from 'date-fns';

import {
	colors,
	isDeviceMinimumSize,
	spacing,
	Text,
	PletlyLogo,
	ImageBackground,
	ToastComponent,
} from '@pletly/ui';
import { AuthContainerProps } from './AuthContainerProps';
import backgroundImage from './bg_web.png';

const shouldShowMaintenanceWarning = isFuture(1741779000000); // March 12th 2025, 12:30PM
const WarningIcon = ToastComponent.icons.warning;

export const AuthContainer = ({ title, subtitle, children }: AuthContainerProps) => {
	const styles = useStyles();
	const deviceSize = useDeviceSize();
	const isDeviceMinLarge = isDeviceMinimumSize(deviceSize, DEVICE_SIZES.LG);

	return (
		<View style={styles.container}>
			<View style={styles.formContainer}>
				<View style={styles.headerContainer}>
					<PletlyLogo width={72} height={56} viewBox="0 0 158 123" />
					{shouldShowMaintenanceWarning && (
						<View style={styles.maintenanceWarning}>
							<WarningIcon width={32} height={32} />
							<Text category="h5" status="danger">
								System maintenance scheduled: Wednesday, March 12th, 9AM-12PM CET
							</Text>
						</View>
					)}
				</View>
				{children}
			</View>
			{isDeviceMinLarge ? (
				<ImageBackground
					source={backgroundImage}
					style={styles.backgroundImageContainer}
					imageStyle={styles.backgroundImage}
					resizeMode="cover"
				>
					<LinearGradient
						colors={['rgba(12, 14, 19, 0)', 'rgba(12, 14, 19, 0.58)', '#0F1D1D']}
						locations={[0.5, 0.72, 1]}
						start={[0.5, 0]}
						end={[0.5, 1]}
						style={styles.backgroundGradient}
					>
						{title && (
							<Text category="p1" style={styles.title} appearance="alternative">
								{title}
							</Text>
						)}
						{subtitle && (
							<Text category="p1" style={styles.subtitle} appearance="alternative">
								{subtitle}
							</Text>
						)}
					</LinearGradient>
				</ImageBackground>
			) : null}
		</View>
	);
};

const useStyles = CreateResponsiveStyle(
	{
		container: {
			flex: 1,
			padding: spacing(4),
			backgroundColor: colors.neutral300,
			flexDirection: 'row',
			gap: spacing(4),
		},
		formContainer: {
			flex: 1,
			justifyContent: 'center',
			paddingHorizontal: spacing(4),
		},
		backgroundImageContainer: {
			flex: 2,
			width: '100%',
			height: '100%',
		},
		backgroundGradient: {
			flex: 1,
			justifyContent: 'flex-end',
			padding: spacing(8),
			borderBottomRightRadius: spacing(4),
			borderBottomLeftRadius: spacing(),
		},
		backgroundImage: {
			borderTopLeftRadius: spacing(4),
			borderBottomRightRadius: spacing(4),
			borderTopRightRadius: spacing(),
			borderBottomLeftRadius: spacing(),
		},
		title: {
			lineHeight: 92,
			fontSize: 56,
		},
		subtitle: {
			lineHeight: 42,
			fontSize: 24,
		},
		headerContainer: {
			flexDirection: 'row',
			alignItems: 'center',
			marginBottom: spacing(2),
		},
		maintenanceWarning: {
			flex: 1,
			gap: spacing(),
			flexDirection: 'row',
			marginLeft: spacing(2),
			alignItems: 'center',
		},
	},
	{
		[DEVICE_SIZES.EXTRA_SMALL_DEVICE]: {
			container: {
				paddingHorizontal: spacing(),
			},
			formContainer: {
				paddingHorizontal: 0,
			},
		},
	},
);
